/**
 * UI-only guards for registry connection rows (secrets catalog).
 * Server remains authoritative; this hides Edit/Delete when the row was created
 * at a layer the viewer does not administer.
 *
 *   - TENANT scope  → only when ``allowTenantRegistryMutate`` is true (Workspace
 *     settings page) **and** workspace OWNER or ADMIN
 *   - NETWORK scope → Cognito sub matches the network row’s designer (``userId`` from GET /networks/{id})
 *   - POOL scope    → pool membership role CREATOR for this pool (agentic room)
 */
import { canAccessWorkspaceAdmin } from './stepflowRoles.js';

const _SCOPE_RANK = { TENANT: 0, NETWORK: 1, POOL: 2 };

/**
 * The Connections surface implied by the catalog context: a room (poolId set)
 * is a POOL surface; a workbench with a network is a NETWORK surface; otherwise
 * it is the tenant Connections surface.
 * @returns {'TENANT'|'NETWORK'|'POOL'}
 */
export function registryCatalogSurface(catalogNetworkId = '', catalogPoolId = '') {
  const catNid = String(catalogNetworkId || '').trim();
  const catPid = String(catalogPoolId || '').trim();
  if (catPid) return 'POOL';
  if (catNid && catNid !== 'new') return 'NETWORK';
  return 'TENANT';
}

/**
 * True when a row is INHERITED at the current surface — its scope sits ABOVE
 * the surface, so it is shown for context but is display-only here (manage it
 * at the scope it was created). Mirrors the DataPlane "Inherited" badge rule
 * and is the same gate ``canEditDeleteRegistrySecret`` uses to lock mutation.
 * @returns {boolean}
 */
export function registrySecretInheritedHere(row, catalogNetworkId = '', catalogPoolId = '') {
  const scope = row?.scope && typeof row.scope === 'object' ? row.scope : {};
  const kind = String(scope.kind || 'TENANT').toUpperCase();
  const surface = registryCatalogSurface(catalogNetworkId, catalogPoolId);
  return (_SCOPE_RANK[kind] ?? 0) < (_SCOPE_RANK[surface] ?? 0);
}

/**
 * @param {{
 *   allowTenantRegistryMutate?: boolean,
 *   row: Record<string, unknown>,
 *   catalogNetworkId?: string,
 *   catalogPoolId?: string,
 *   workspaceRole?: string,
 *   poolRole?: string,
 *   viewerUserSub?: string,
 *   networkOwnerUserId?: string,
 * }} args
 * @returns {boolean}
 */
export function canEditDeleteRegistrySecret({
  row,
  catalogNetworkId = '',
  catalogPoolId = '',
  workspaceRole = '',
  poolRole = '',
  viewerUserSub = '',
  networkOwnerUserId = '',
  allowTenantRegistryMutate = false,
}) {
  const scope = row?.scope && typeof row.scope === 'object' ? row.scope : {};
  const kind = String(scope.kind || 'TENANT').toUpperCase();
  const pr = String(poolRole || '').toUpperCase();
  const sub = String(viewerUserSub || '').trim();
  const netOwner = String(networkOwnerUserId || '').trim();

  const catNid = String(catalogNetworkId || '').trim();
  const catPid = String(catalogPoolId || '').trim();
  const rowNid = String(scope.networkId ?? scope.network_id ?? '').trim();
  const rowPid = String(scope.poolId ?? scope.pool_id ?? '').trim();

  // Down-only inheritance: a row may only be mutated from its OWN surface, never
  // from a child surface that merely inherits it. A row whose scope sits ABOVE
  // the surface is inherited → display-only (mirrors the Context feature). This
  // is the UI half; the server enforces the same rule on delete/PUT via
  // _validate_scope_for_write.
  if (registrySecretInheritedHere(row, catalogNetworkId, catalogPoolId)) return false;

  if (kind === 'TENANT') {
    if (!allowTenantRegistryMutate) return false;
    return canAccessWorkspaceAdmin(workspaceRole);
  }

  if (kind === 'NETWORK') {
    if (!rowNid || rowNid !== catNid) return false;
    if (!sub || !netOwner) return false;
    return sub === netOwner;
  }

  if (kind === 'POOL') {
    if (!rowNid || rowNid !== catNid || !rowPid || rowPid !== catPid) return false;
    return pr === 'CREATOR';
  }

  return false;
}
